// src/components/auth/ResetPassword/ResetPassword.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Alert,
    InputAdornment,
    Divider,
    CircularProgress,
    Chip,
    IconButton,
} from '@mui/material';
import {
    Email as EmailIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Security as SecurityIcon,
    Shield as ShieldIcon,
    Lock as LockIcon,
    Verified as VerifiedIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    ArrowForward as ArrowForwardIcon,
    Storefront as StorefrontIcon,
    NetworkCheck as WifiIcon,
    NetworkCheck as CellTowerIcon,
    NetworkCheck as RouterIcon,
    SettingsApplications as HubIcon,
    Speed as SpeedIcon,
    Cloud as CloudIcon,
    Language as LanguageIcon,
    NetworkCheck as SignalIcon,
    MarkEmailReadRounded as MarkEmailReadIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../AuthContext';
import '../Login/Login.css';
import BrandLogo from '../../common/BrandLogo';
import { friendlyError } from '../../../utils/userMessages';

const ResetPassword = () => {
    const navigate = useNavigate();
    const { resetPassword } = useAuth();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const [resendTimer, setResendTimer] = useState(0);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const canvasRef = useRef(null);

    // Particle network animation - Same as Login
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationId;
        let particles = [];

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener('resize', resize);

        class Particle {
            constructor() {
                this.reset();
            }

            reset() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.size = Math.random() * 2 + 0.5;
                this.speedX = (Math.random() - 0.5) * 0.5;
                this.speedY = (Math.random() - 0.5) * 0.5;
                this.opacity = Math.random() * 0.5 + 0.1;
                this.color = [
                    'rgba(216, 78, 23,',
                    'rgba(165, 57, 12,',
                    'rgba(255, 140, 66,',
                    'rgba(52, 73, 94,',
                    'rgba(142, 68, 173,'
                ][Math.floor(Math.random() * 5)];
            }

            update() {
                this.x += this.speedX;
                this.y += this.speedY;
                if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
                if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
            }

            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = `${this.color} ${this.opacity})`;
                ctx.fill();
            }
        }

        for (let i = 0; i < 80; i++) {
            particles.push(new Particle());
        }

        const connectParticles = () => {
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const dx = particles[i].x - particles[j].x;
                    const dy = particles[i].y - particles[j].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    if (distance < 150) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(216, 78, 23, ${0.06 * (1 - distance / 150)})`;
                        ctx.lineWidth = 0.5;
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(particles[j].x, particles[j].y);
                        ctx.stroke();
                    }
                }
            }
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => { p.update(); p.draw(); });
            connectParticles();
            animationId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationId);
        };
    }, []);

    // Mouse parallax - Same as Login
    useEffect(() => {
        const handleMouseMove = (e) => {
            setMousePos({
                x: (e.clientX / window.innerWidth - 0.5) * 2,
                y: (e.clientY / window.innerHeight - 0.5) * 2
            });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Floating icons - Same as Login
    const floatingIcons = [
        { Icon: WifiIcon, delay: 0, x: '10%', y: '15%', size: 28 },
        { Icon: CellTowerIcon, delay: 0.5, x: '85%', y: '20%', size: 32 },
        { Icon: RouterIcon, delay: 1, x: '15%', y: '75%', size: 24 },
        { Icon: HubIcon, delay: 1.5, x: '80%', y: '70%', size: 26 },
        { Icon: SpeedIcon, delay: 2, x: '50%', y: '10%', size: 22 },
        { Icon: CloudIcon, delay: 2.5, x: '70%', y: '85%', size: 30 },
        { Icon: LanguageIcon, delay: 3, x: '25%', y: '90%', size: 26 },
        { Icon: SignalIcon, delay: 3.5, x: '90%', y: '45%', size: 24 },
    ];

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return 'Email is required';
        if (!emailRegex.test(email)) return 'Please enter a valid email address';
        return '';
    };

    const emailError = email ? validateEmail(email) : '';
    const isFormValid = !emailError && email;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationError = validateEmail(email);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        setIsLoading(true);

        try {
            await resetPassword(email);
            setIsSuccess(true);
            setResendTimer(60);
            const timer = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (err) {
            console.error('Password reset error:', err);
            setError(friendlyError(err, 'Failed to request the reset link. Please try again.'));
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendTimer > 0) return;
        setError('');
        setIsLoading(true);
        try {
            await resetPassword(email);
            setResendTimer(60);
            const timer = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (err) {
            console.error('Resend error:', err);
            setError(friendlyError(err, 'Failed to resend the reset link.'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            setResendTimer(0);
        };
    }, []);

    return (
        <Box className="login-page-v2">
            {/* Canvas Particle Network */}
            <canvas ref={canvasRef} className="particle-canvas" />

            {/* Animated Background Elements */}
            <Box className="bg-elements">
                <motion.div
                    className="gradient-orb orb-primary"
                    animate={{ x: mousePos.x * 30, y: mousePos.y * 30 }}
                    transition={{ type: 'spring', stiffness: 50, damping: 30 }}
                />
                <motion.div
                    className="gradient-orb orb-secondary"
                    animate={{ x: mousePos.x * -20, y: mousePos.y * -20 }}
                    transition={{ type: 'spring', stiffness: 50, damping: 30 }}
                />
                <motion.div
                    className="gradient-orb orb-tertiary"
                    animate={{ x: mousePos.x * 15, y: mousePos.y * -15 }}
                    transition={{ type: 'spring', stiffness: 50, damping: 30 }}
                />
                <div className="gradient-orb orb-accent" />

                {floatingIcons.map(({ Icon, delay, x, y, size }, index) => (
                    <motion.div
                        key={index}
                        className="floating-icon"
                        style={{ left: x, top: y }}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                            opacity: [0.08, 0.15, 0.08],
                            scale: [1, 1.1, 1],
                            y: [0, -15, 0],
                            rotate: [0, 5, -5, 0],
                        }}
                        transition={{ duration: 6, delay, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        <Icon sx={{ fontSize: size, color: '#d84e17' }} />
                    </motion.div>
                ))}

                <motion.div
                    className="geo-shape shape-ring"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                />
                <motion.div
                    className="geo-shape shape-ring-2"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
                />
                <motion.div
                    className="geo-shape shape-dots"
                    animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.05, 1] }}
                    transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div className="bg-grid-pattern" />
                <motion.div
                    className="scan-line"
                    animate={{ y: ['0vh', '100vh'] }}
                    transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                />
            </Box>

            <Container maxWidth="xl" className="login-container-v2">
                <Box className="login-layout">

                    {/* ── Left Brand Section ── */}
                    <motion.div
                        className="brand-section"
                        initial={{ opacity: 0, x: -60 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <Box className="brand-inner">

                            {/* Logo */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Box className="brand-logo-group">
                                    <motion.div
                                        className="logo-icon-container"
                                        whileHover={{ scale: 1.05, rotate: 5 }}
                                        transition={{ type: 'spring', stiffness: 300 }}
                                    >
                                        <BrandLogo scope="platform" size={64} radius={18} alt="TELCOFAIR" sx={{ position: 'relative', zIndex: 2 }} />
                                        <Box className="logo-pulse-ring" />
                                        <Box className="logo-pulse-ring ring-2" />
                                    </motion.div>
                                    <Box>
                                        <Typography className="brand-name">TELCOFAIR</Typography>
                                        <Typography className="brand-tagline">Telecom Business Hub</Typography>
                                    </Box>
                                </Box>
                            </motion.div>

                            {/* Hero Text - Modified for Reset Password */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                            >
                                <Typography className="hero-headline">
                                    Reset Your
                                    <span className="gradient-text"> Password</span>
                                    <br />
                                    <span style={{ fontSize: '1.8rem' }}>In Just Few Steps</span>
                                </Typography>
                                <Typography className="hero-description">
                                    We'll send you a secure link to reset your password
                                    and regain access to your account.
                                </Typography>
                            </motion.div>

                            {/* Feature Cards - Modified for Reset Password */}
                            <motion.div
                                className="feature-cards"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                            >
                                {[
                                    { icon: <ShieldIcon />, title: 'Secure Process', desc: 'Industry-standard encryption', color: '#d84e17' },
                                    { icon: <MarkEmailReadIcon />, title: 'Instant Delivery', desc: 'Reset link sent to your inbox', color: '#1f9d55' },
                                    { icon: <LockIcon />, title: 'Account Protection', desc: 'Keep your account safe', color: '#6a1b9a' },
                                ].map((feature, i) => (
                                    <motion.div
                                        key={i}
                                        className="feature-card-item"
                                        whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                    >
                                        <Box
                                            className="feature-card-icon"
                                            sx={{ background: `${feature.color}15`, color: feature.color }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <Box>
                                            <Typography className="feature-card-title">{feature.title}</Typography>
                                            <Typography className="feature-card-desc">{feature.desc}</Typography>
                                        </Box>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Stats Bar - Modified for Reset Password */}
                            <motion.div
                                className="stats-bar"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.8 }}
                            >
                                {[
                                    { number: '24/7', label: 'Support' },
                                    { number: '5min', label: 'Average Reset' },
                                    { number: '100%', label: 'Secure' },
                                ].map((stat, i) => (
                                    <React.Fragment key={i}>
                                        {i > 0 && <Box className="stats-divider" />}
                                        <Box className="stat-block">
                                            <Typography className="stat-value">{stat.number}</Typography>
                                            <Typography className="stat-text">{stat.label}</Typography>
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </motion.div>
                        </Box>
                    </motion.div>

                    {/* ── Right Reset Password Form ── */}
                    <motion.div
                        className="form-section"
                        initial={{ opacity: 0, x: 60 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                    >
                        <Paper elevation={0} className="form-card">
                            <Box className="form-top-accent" />

                            {/* Corner decorations */}
                            <Box className="form-corner-decor top-left" />
                            <Box className="form-corner-decor top-right" />
                            <Box className="form-corner-decor bottom-left" />
                            <Box className="form-corner-decor bottom-right" />

                            <Box className="form-content">

                                {/* Header */}
                                <Box className="form-header">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.5 }}
                                    >
                                        <Box className="form-header-icon">
                                            <MarkEmailReadIcon sx={{ fontSize: 24, color: '#d84e17' }} />
                                        </Box>
                                    </motion.div>
                                    <Typography className="form-title">Reset Password</Typography>
                                    <Typography className="form-subtitle">
                                        Enter your email to receive a reset link
                                    </Typography>
                                </Box>

                                {/* Error Alert */}
                                <AnimatePresence>
                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10, height: 0 }}
                                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                                            exit={{ opacity: 0, y: -10, height: 0 }}
                                        >
                                            <Alert
                                                severity="error"
                                                className="form-alert"
                                                icon={<SecurityIcon />}
                                                onClose={() => setError('')}
                                            >
                                                {error}
                                            </Alert>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {isSuccess ? (
                                    /* Success State */
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5 }}
                                        className="success-container"
                                    >
                                        <Box className="success-content">
                                            <motion.div
                                                animate={{
                                                    scale: [1, 1.2, 1],
                                                    rotate: [0, 10, -10, 0],
                                                }}
                                                transition={{ duration: 0.8 }}
                                            >
                                                <CheckCircleIcon
                                                    sx={{
                                                        fontSize: 80,
                                                        color: '#10b981',
                                                        mb: 2,
                                                    }}
                                                />
                                            </motion.div>
                                            <Typography className="success-title">
                                                Check Your Email
                                            </Typography>
                                            <Typography className="success-text">
                                                We've sent a password reset link to <strong>{email}</strong>
                                            </Typography>

                                            <Alert
                                                severity="info"
                                                className="success-alert"
                                            >
                                                <Typography variant="body2" sx={{ color: '#5a4a3b' }}>
                                                    📧 Please check your spam folder if you don't see the email within 5 minutes.
                                                </Typography>
                                            </Alert>

                                            <Box className="success-actions">
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleResend}
                                                    disabled={resendTimer > 0}
                                                    className="resend-btn"
                                                >
                                                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Link'}
                                                </Button>

                                                <Button
                                                    component={Link}
                                                    to="/login"
                                                    variant="contained"
                                                    className="back-login-btn"
                                                    startIcon={<ArrowBackIcon />}
                                                >
                                                    Back to Login
                                                </Button>
                                            </Box>
                                        </Box>
                                    </motion.div>
                                ) : (
                                    /* Reset Password Form */
                                    <motion.form
                                        onSubmit={handleSubmit}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: 0.3 }}
                                        className="login-form"
                                    >
                                        <Box className="input-group">
                                            {/* Email Field */}
                                            <motion.div
                                                className={`input-wrapper ${focusedField === 'email' ? 'focused' : ''}`}
                                                animate={
                                                    focusedField === 'email'
                                                        ? { boxShadow: '0 0 0 3px rgba(216, 78, 23, 0.1)' }
                                                        : { boxShadow: '0 0 0 0px rgba(216, 78, 23, 0)' }
                                                }
                                            >
                                                <Box className="input-icon-box">
                                                    <EmailIcon sx={{ fontSize: 20 }} />
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    label="Email Address"
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                    placeholder="name@company.com"
                                                    onFocus={() => setFocusedField('email')}
                                                    onBlur={() => setFocusedField(null)}
                                                    variant="standard"
                                                    className="custom-input"
                                                    autoComplete="email"
                                                    slotProps={{ input: { disableUnderline: true } }}
                                                />
                                            </motion.div>
                                        </Box>

                                        {/* Info Alert */}
                                        <Alert
                                            severity="info"
                                            className="info-alert"
                                        >
                                            <Typography variant="body2" sx={{ color: '#5a4a3b' }}>
                                                🔑 We'll send a password reset link to this email address.
                                            </Typography>
                                        </Alert>

                                        {/* Submit Button */}
                                        <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                                            <Button
                                                type="submit"
                                                fullWidth
                                                variant="contained"
                                                disabled={isLoading || !isFormValid}
                                                className="submit-btn"
                                                endIcon={
                                                    !isLoading &&
                                                    <ArrowForwardIcon sx={{ fontSize: 20 }} />
                                                }
                                            >
                                                {isLoading ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                                                        <span>Sending...</span>
                                                    </Box>
                                                ) : (
                                                    'Send Reset Link'
                                                )}
                                            </Button>
                                        </motion.div>
                                    </motion.form>
                                )}

                                {/* Security Badges */}
                                <Box className="security-section">
                                    <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.05)' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#b0a090',
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            Secured Platform
                                        </Typography>
                                    </Divider>
                                    <Box className="security-badges">
                                        {[
                                            { icon: <LockIcon />, label: 'AES-256' },
                                            { icon: <VerifiedIcon />, label: 'Verified' },
                                            { icon: <ShieldIcon />, label: 'SSL/TLS' },
                                        ].map((badge, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 1 + i * 0.1 }}
                                            >
                                                <Chip
                                                    icon={React.cloneElement(badge.icon, {
                                                        sx: { fontSize: '14px !important' }
                                                    })}
                                                    label={badge.label}
                                                    size="small"
                                                    className="security-chip"
                                                />
                                            </motion.div>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Register CTA - Modified for Reset Password */}
                                <motion.div
                                    className="register-cta"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.2 }}
                                >
                                    <Typography className="register-text">
                                        Remember your password?
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to="/login"
                                        className="register-btn"
                                        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                                        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                    >
                                        Back to Login
                                    </Button>
                                </motion.div>

                            </Box>
                        </Paper>
                    </motion.div>

                </Box>
            </Container>
        </Box>
    );
};

export default ResetPassword;